/**
 * RosterImageSection
 *
 * VIEW-ONLY roster image browser for web. A coach searches a team, and the
 * team's current roster image is resolved and displayed. Annotations are
 * authored on iPad as separate notes drawn over the roster; web never offers an
 * authoring/drawing surface and shows only the current roster image.
 *
 * States [SpecFlow I3]: loading · resolved · empty (no roster yet → upload CTA)
 * · error (sign failure / missing object / expired URL → retry, never a broken
 * image).
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../../../components/Button';
import { RosterUploadModal } from '../players/RosterUploadModal';
import { useTeamRosterImage } from '../../../../hooks/useTeamRosterImage';
import { RosterTeamSearch } from './RosterTeamSearch';
import { RosterImageDisplay } from './RosterImageDisplay';
import './RosterImageSection.css';

interface SelectedTeam {
  id: string;
  name: string;
}

export function RosterImageSection() {
  const [selectedTeam, setSelectedTeam] = useState<SelectedTeam | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Preselect a team when arrived at via "View Roster" (team actions modal),
  // skipping the search box. Consume the params once so a later manual Clear
  // isn't undone by the still-present query string.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const teamId = searchParams.get('teamId');
    const teamName = searchParams.get('teamName');
    if (teamId && teamName) {
      setSelectedTeam({ id: teamId, name: teamName });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Set when the PLAIN roster image's signed URL fails even after one re-resolve,
  // so we surface an error state instead of leaving a broken <img> mounted.
  const [plainImageFailed, setPlainImageFailed] = useState(false);

  const { status, imageUrl, errorMessage, retry } = useTeamRosterImage(
    selectedTeam?.id ?? null,
  );

  // One-shot guard so an expired plain-roster signed URL recovers via a single
  // re-resolve instead of looping `onError` → retry → onError forever.
  const hasRetriedImageRef = useRef(false);

  const selectTeam = (team: { id: string; name: string }) => {
    setSelectedTeam({ id: team.id, name: team.name });
    hasRetriedImageRef.current = false;
    setPlainImageFailed(false);
  };

  const handleClear = () => {
    setSelectedTeam(null);
    hasRetriedImageRef.current = false;
    setPlainImageFailed(false);
  };

  const handleRetry = () => {
    hasRetriedImageRef.current = false;
    setPlainImageFailed(false);
    retry();
  };

  const handlePlainImageError = () => {
    // A loaded signed URL can still 403 once it expires; recover once, then
    // surface an error rather than rendering the browser's broken-image glyph.
    if (hasRetriedImageRef.current) {
      setPlainImageFailed(true);
      return;
    }
    hasRetriedImageRef.current = true;
    retry();
  };

  const handlePlainImageLoad = () => {
    // A fresh URL loaded successfully — restore the one-shot retry budget so a
    // later expiry of THIS url gets its own recovery attempt (otherwise a
    // long-open viewer hard-errors on the second expiry).
    hasRetriedImageRef.current = false;
  };

  const handleUploadSuccess = () => {
    setIsUploadModalOpen(false);
    hasRetriedImageRef.current = false;
    setPlainImageFailed(false);
    // The upload modal creates/imports teams via the shared roster-upload flow.
    // Re-resolve the current selection so a roster image set for it becomes
    // visible. (Web's CSV/import flow does not thread the originating upload id,
    // so the canonical image may still be unset — the re-resolve reflects the
    // true server state rather than presuming success.)
    retry();
  };

  return (
    <div className="roster-image-section">
      <header className="roster-image-section__header">
        <h1 className="roster-image-section__title">Roster Images</h1>
        <p className="roster-image-section__subtitle">
          Search a team to view its current roster image. Annotations are
          created on iPad — this view is read-only.
        </p>
      </header>

      <div className="roster-image-section__search">
        <RosterTeamSearch
          selectedTeamName={selectedTeam?.name ?? null}
          onSelectTeam={selectTeam}
          onClear={handleClear}
        />
      </div>

      <div className="roster-image-section__viewport">
        {!selectedTeam ? (
          <div className="roster-image-section__placeholder">
            <p>Search for a team to view its roster image.</p>
          </div>
        ) : status === 'loading' ? (
          <div
            className="roster-image-section__loading"
            role="status"
            aria-live="polite"
          >
            <div className="roster-image-section__spinner" />
            <span>Loading roster image…</span>
          </div>
        ) : status === 'empty' ? (
          <div className="roster-image-section__state" data-testid="roster-empty-state">
            <h2 className="roster-image-section__state-title">
              No roster image yet
            </h2>
            <p className="roster-image-section__state-text">
              {selectedTeam.name} doesn’t have a roster image. Upload one to get
              started.
            </p>
            <Button variant="primary" onClick={() => setIsUploadModalOpen(true)}>
              Upload roster
            </Button>
          </div>
        ) : status === 'error' || plainImageFailed ? (
          <div className="roster-image-section__state" role="alert" data-testid="roster-error-state">
            <h2 className="roster-image-section__state-title">
              Couldn’t load roster image
            </h2>
            <p className="roster-image-section__state-text">
              {plainImageFailed
                ? 'This team’s roster image could not be loaded — its link may have expired.'
                : errorMessage ??
                  'Something went wrong resolving this team’s roster image.'}
            </p>
            <Button variant="secondary" onClick={handleRetry}>
              Try again
            </Button>
          </div>
        ) : status === 'resolved' && imageUrl ? (
          <RosterImageDisplay
            teamName={selectedTeam.name}
            rosterImageUrl={imageUrl}
            onPlainImageError={handlePlainImageError}
            onPlainImageLoad={handlePlainImageLoad}
          />
        ) : null}
      </div>

      <RosterUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
}
